// ============================================================================
// todo-client-mod -- useTodos Hook
//
// 获取当前用户的 Todo 列表，支持分组过滤、分页、状态过滤和排序。
// 支持 SSR：传入 initialData 则直接使用（不请求）。
// ============================================================================
import { useState, useEffect, useCallback, useRef } from 'react';
import { getTodoApiClient } from '../todo-api-client.js';
/**
 * 获取 Todo 列表
 *
 * @example
 * ```tsx
 * const { todos, loading, error, setPage, setGroupId } = useTodos({
 *   groupId: selectedGroupId,
 *   pageSize: 10,
 * })
 * ```
 */
export function useTodos(params) {
    const initialData = params?.initialData;
    const [todos, setTodos] = useState(initialData?.data ?? []);
    const [pagination, setPagination] = useState(initialData?.pagination ?? null);
    const [loading, setLoading] = useState(!initialData);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(params?.page ?? 1);
    const [groupId, setGroupId] = useState(params?.groupId);
    const [status, setStatusFilter] = useState(params?.status);
    const isFirstRender = useRef(true);
    const fetchTodos = useCallback(async () => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            if (initialData)
                return;
        }
        setLoading(true);
        setError(null);
        try {
            const result = await getTodoApiClient().getTodos({
                page,
                pageSize: params?.pageSize ?? 10,
                status,
                groupId,
                priority: params?.priority,
                tags: params?.tags,
                sortBy: params?.sortBy,
                sortOrder: params?.sortOrder,
            });
            setTodos(result.data);
            setPagination(result.pagination);
        }
        catch (err) {
            const message = err instanceof Error ? err.message : '获取待办事项失败';
            setError(message);
            setTodos([]);
            setPagination(null);
        }
        finally {
            setLoading(false);
        }
    }, [page, groupId, status, params?.pageSize, params?.priority, params?.tags, params?.sortBy, params?.sortOrder, initialData]);
    useEffect(() => {
        fetchTodos();
    }, [fetchTodos]);
    return {
        todos,
        pagination,
        loading,
        error,
        refresh: fetchTodos,
        setPage,
        setGroupId,
        setStatus: setStatusFilter,
    };
}
//# sourceMappingURL=use-todos.js.map